import ServiceEndpoints from "../ServiceEndpoints";
import {IRequestForInfoItem, IRequestForInfoItemError} from "../../model/RequestForInfoItem";
import {INewRequestForInfoItem, INewRequestForInfoItemError} from "../../model/NewRequestForInfoItem";
import {NewRequestDto} from "../../model/NewRequestDto";
import {HTTP_STATUS_UNAUTHORIZED} from "../../constants/HttpStatusCodes";
import {WorkflowHistoryItem, WorkflowHistoryItemError} from "../../model/WorkflowHistoryItem";
import {CAResponseDto, CAResponseDtoError, ResponseItem} from "../../model/ResponseItem";
import {NewRequestEmailError} from "../../model/NewRequestEmailError";

export async function fetchRequestInfoById(requestId: string, userToken?:string): Promise<IRequestForInfoItem | IRequestForInfoItemError> {
    return await fetch(ServiceEndpoints.FetchActiveRequestById + requestId,
        { method: 'get', headers: { "Content-Type": "application/json", "Authorization": "Bearer "+userToken}})
        .then(async (resp) => {
            if (resp.status === HTTP_STATUS_UNAUTHORIZED) {
                return {statusCode: HTTP_STATUS_UNAUTHORIZED};
            }
            if (resp.ok) {
                const parsedData: IRequestForInfoItem = await resp.json();
                return parsedData;
            }
            const errorData = await resp.json();
            throw new Error(errorData.message ?? (errorData.errors?.join(', ') ?? 'Some error occurred.'));
        })
        .catch((error) => {
            console.error(error);
            throw new Error(error);
        });
}

export async function fetchNewRequestInfoById(requestId: string, assessmentIdArray: string[], userToken?:string, draftId?: string): Promise<INewRequestForInfoItem | INewRequestForInfoItemError> {
    const url = ServiceEndpoints.FetchNewRequestInfoById + requestId + (draftId ? "?reqId=" + draftId : "");
    return await fetch(url,
        { method: 'post', headers: { "Content-Type": "application/json", "Authorization": "Bearer "+userToken}, body: JSON.stringify(assessmentIdArray)})
        .then(async (resp) => {
            if (resp.status === HTTP_STATUS_UNAUTHORIZED) {
                return {statusCode: HTTP_STATUS_UNAUTHORIZED};
            }
            const parsedData: INewRequestForInfoItem = await resp.json();
            return parsedData;
        })
        .catch((error) => {
            console.error(error);
            throw new Error(error);
        });
}

export async function postNewRequest(newRequest: NewRequestDto, userToken?:string): Promise<string | NewRequestEmailError> {
    newRequest.Requests.forEach((req: NewRequestDto) => {
        if (req.ncaDto) {
            delete (req.ncaDto as any).Name;
        }
    });
    return await fetch(ServiceEndpoints.CreateNewRequest,
        { method: 'post', headers: { "Content-Type": "application/json", "Authorization": "Bearer "+userToken}, body: JSON.stringify(newRequest)})
        .then(async (resp) => {
            if (resp.ok) {
                return resp.json();
            }
            const errorData = await resp.json();
            const data = errorData.data?.requestId;
            if (data) {
                return {reqId: data, message: errorData.errors?.join(', ')};
            }
            throw new Error(errorData.message ?? (errorData.errors?.join(', ') ?? 'Some error occurred.'));
        })
        .catch((error) => {
            console.error(error);
            throw new Error(error);
        });
}

export async function putDraftRequest(draftRequest: NewRequestDto, draftId: string, userToken?:string): Promise<number> {
    draftRequest.Requests.forEach((req: NewRequestDto) => {
        if (req.ncaDto) {
            delete (req.ncaDto as any).Name;
        }
    });
    return await fetch(ServiceEndpoints.UpdateDraftRequest + draftId,
        { method: 'put', headers: { "Content-Type": "application/json", "Authorization": "Bearer "+userToken}, body: JSON.stringify(draftRequest)})
        .then(async (resp) => {
            if (resp.ok) {
                const parsedData = resp.json();
                console.log(parsedData)
                return parsedData;
            }
            const errorData = await resp.json();
            console.error(errorData);
            throw new Error(errorData.message ?? (errorData.errors?.join(', ') ?? 'Some error occurred.'));
        })
        .catch((error) => {
            console.error(error);
            throw new Error(error);
        });
}

export async function fetchHistoryById(requestId: string, userToken?:string): Promise<WorkflowHistoryItem[] | WorkflowHistoryItemError> {
    return await fetch(ServiceEndpoints.FetchWorkflowHistoyById + requestId,
        { method: 'get', headers: { "Content-Type": "application/json", "Authorization": "Bearer "+userToken}})
        .then(async (resp) => {
            if (resp.status === HTTP_STATUS_UNAUTHORIZED) {
                return {statusCode: HTTP_STATUS_UNAUTHORIZED};
            }
            if (resp.ok) {
                const parsedData: WorkflowHistoryItem[] = await resp.json();
                return parsedData;
            }
            const errorData = await resp.json();
            throw new Error(errorData.message ?? (errorData.errors?.join(', ') ?? 'Some error occurred.'));
        })
        .catch((error) => {
            console.error(error);
            throw new Error(error);
        });
}

export async function updateRequest(requestId: string, body: ResponseItem, userToken?:string): Promise<CAResponseDto | CAResponseDtoError> {
    return await fetch(ServiceEndpoints.UpdateCaResponse + requestId,
        { method: 'put', headers: { "Content-Type": "application/json", "Authorization": "Bearer "+userToken}, body: JSON.stringify(body)})
        .then(async (resp) => {
            if (resp.status === HTTP_STATUS_UNAUTHORIZED) {
                return {statusCode: HTTP_STATUS_UNAUTHORIZED};
            }
            if (resp.ok) {
                const parsedData: CAResponseDto = await resp.json();
                return parsedData;
            }
            const errorData = await resp.json();
            throw new Error(errorData.message ?? (errorData.errors?.join(', ') ?? 'Some error occurred.'));
        })
        .catch((error) => {
            console.error(error);
            throw new Error(error);
        });
}

export async function pdfExportResponse(id: string, filename: string, userToken?: string): Promise<boolean> {
    return await fetch(ServiceEndpoints.GenerateReport + id,
        {
            method: 'post',
            headers: {"Content-Type": "application/json", "Authorization": "Bearer " + userToken},
        })
        .then(async (resp) => {
            if(resp.ok) {
                const contentDisposition = resp.headers.get('Content-Disposition');
                const filenameMatch = contentDisposition?.match(/filename\*=UTF-8''(.+)/);
                const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : 'download.pdf';
                const blob = await resp.blob();
                if(blob) {
                    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", filename);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.open(url, "_blank", "opener=yes");
                    return true;
                }
            }
            const errorData = await resp.json();
            throw new Error(errorData.message ?? (errorData.errors?.join(', ') ?? 'Some error occurred.'));
        })
        .catch((error) => {
            console.error(error);
            throw new Error(error);
        });
}
